export interface Challenge {
  id: string;
  title: string;
  description: string;
  type: 'choice' | 'estimate';
  question: string;
  options?: string[];
  correctAnswer: any[];
  explanation: string;
  points: number;
  bias?: string;
  hint?: string;
  anchor?: number;
  acceptableRange?: [number, number];
}

export interface ChallengeStage {
  id: string;
  title: string;
  description: string;
  challenges: Challenge[];
}

export interface ChallengePath {
  id: string;
  title: string;
  description: string;
  stages: ChallengeStage[];
  badge: string;
}

export const challengePaths: ChallengePath[] = [
  {
    id: 'rookie-trader-academy',
    title: "🎯 Rookie Trader Academy",
    description: "Master the basics while avoiding the rookie mistakes that blow up accounts.",
    badge: 'Disciplined Trader',
    stages: [
      {
        id: 'first-trade-survival',
        title: 'Stage 1: First Trade Survival',
        description: 'Learn to survive your first trades without blowing up your account',
        challenges: [
          {
            id: 'rookie-1',
            title: 'The YOLO Trade',
            description: 'You have $10,000 to start trading. Your friend just made 500% on GameStop.',
            type: 'choice',
            question: `You have $10,000 for trading. Your friend shows screenshots of turning $2,000 into $10,000 on a meme stock in one week. What's your first move?`,
            options: [
              'Put all $10,000 into the same stock - follow the money!',
              'Start with $500 to test your strategy',
              'Research the company fundamentals first',
              'Paper trade for a month before risking real money'
            ],
            correctAnswer: [1, 3],
            explanation: `Rookie mistake #1: Going all-in on your first trade. Professional traders risk 1-2% per trade. Paper trading and small position sizing prevent account blow-ups.`,
            points: 100,
            bias: 'overconfidence',
            hint: 'What would a professional trader risk on their first trade?'
          },
          {
            id: 'rookie-2',
            title: 'The Loss That Won\'t Quit',
            description: 'Your first stock is down 15%. Everyone says "just hold, it will come back."',
            type: 'choice',
            question: `You bought Tesla at $300, now it's $255 (-15%). Your trading group says "diamond hands, it always comes back!" What do you do?`,
            options: [
              'Hold and wait for it to recover',
              'Buy more to average down',
              'Cut the loss at -20% max',
              'Hold until break-even no matter what'
            ],
            correctAnswer: [2],
            explanation: `Rookie mistake #2: No exit plan. Professional traders cut losses at 7-10%. "It always comes back" destroyed more accounts than any other phrase in trading.`,
            points: 150,
            bias: 'loss-aversion',
            hint: 'What would protect your capital for future opportunities?'
          },
          {
            id: 'rookie-3',
            title: 'The Hot Tip Gold Rush',
            description: 'A Discord channel with 50,000 members is pumping a "guaranteed winner."',
            type: 'choice',
            question: `"MoonRocket Corp" is being hyped by a huge trading Discord. The DD looks solid, 50K members are buying. They say "this is financial advice!" What's your move?`,
            options: [
              'Buy immediately before you miss out',
              'Wait for a dip to enter',
              'Check who\'s actually making money from these tips',
              'Only invest what you can afford to lose'
            ],
            correctAnswer: [2, 3],
            explanation: `Rookie mistake #3: Following the crowd. Most "tip" groups are pump-and-dumps. Always check if the tipsters are actually profitable long-term.`,
            points: 200,
            bias: 'social-proof',
            hint: 'Who profits when thousands follow the same tip?'
          },
          {
            id: 'rookie-4',
            title: 'The Revenge Trade',
            description: 'You just lost $2,000 on a bad trade. You want to make it back immediately.',
            type: 'choice',
            question: `You lost $2,000 today on a stupid mistake. You want to win it back before market close. You find a volatile stock that could 2x quickly. What do you do?`,
            options: [
              'Go all-in to win back the loss',
              'Risk double to make back the money faster',
              'Take a break and analyze what went wrong',
              'Make smaller trades to slowly recover'
            ],
            correctAnswer: [2],
            explanation: `Rookie mistake #4: Revenge trading. Emotional trading leads to bigger losses. Take a break, analyze mistakes, come back with a clear head tomorrow.`,
            points: 200,
            bias: 'loss-aversion',
            hint: 'What would a professional do after a big loss?'
          },
          {
            id: 'rookie-5',
            title: 'The Beginner\'s Luck Trap',
            description: 'Your first 5 trades were all winners. You\'re feeling invincible.',
            type: 'estimate',
            question: `You've won 5 trades in a row, turning $5,000 into $8,000. You feel like you've "cracked the code." What's the realistic probability your next 5 trades will also be winners?`,
            correctAnswer: [15, 25],
            explanation: `Rookie mistake #5: Confusing luck with skill. Early success is often random. The market will humble you. Most profitable traders win 40-60% of trades, not 100%.`,
            points: 250,
            bias: 'overconfidence',
            acceptableRange: [5, 40],
            hint: 'Even the best traders lose regularly'
          },
          {
            id: 'rookie-6',
            title: 'The Sunk Cost Cage',
            description: 'You\'ve spent 6 months learning options trading but keep losing money.',
            type: 'choice',
            question: `You spent 6 months learning options trading, bought courses, joined Discord groups. You're down $5,000 but feel like you're "almost there." Your friend suggests switching to simple buy-and-hold. What do you do?`,
            options: [
              'Keep trading options - you\'ve invested too much time to quit',
              'Switch to simple investing - cut your losses on complexity',
              'Try harder with options - you just need more time',
              'Take a break from options but plan to return later'
            ],
            correctAnswer: [1],
            explanation: `This is "The Sunk Cost Cage" - continuing something that no longer serves you because of past investment. Your 6 months of learning is a sunk cost. Sometimes the simple path is the profitable path.`,
            points: 250,
            bias: 'sunk-cost-fallacy'
          }
        ]
      }
    ]
  },
  {
    id: 'wall-street-legends',
    title: "🏛️ Wall Street Legends",
    description: "Learn from the greatest investors and their biggest mistakes. Even legends get humbled.",
    badge: 'Market Historian',
    stages: [
      {
        id: 'legend-lessons',
        title: 'Stage 1: Legend Lessons',
        description: 'Learn what made the greats great (and what brought them down)',
        challenges: [
          {
            id: 'legend-1',
            title: 'Buffett\'s Biggest Mistake',
            description: 'Even Warren Buffett admits to massive investing mistakes.',
            type: 'choice',
            question: `Warren Buffett called buying Berkshire Hathaway "the $200 billion mistake." He also avoided tech stocks for decades, missing Microsoft, Amazon, Google. What's the lesson?`,
            options: [
              'Even legends make huge mistakes',
              'Stick to what you understand, never adapt',
              'Value investing is outdated',
              'You should copy everything Buffett does'
            ],
            correctAnswer: [0],
            explanation: `Legend lesson: Even the GOAT admits massive mistakes. Buffett's humility and willingness to adapt (eventually buying Apple) is what made him great. Nobody's perfect.`,
            points: 150,
            bias: 'overconfidence'
          },
          {
            id: 'legend-2',
            title: 'Soros Breaks the Bank',
            description: 'George Soros made $1 billion shorting the British pound in 1992.',
            type: 'estimate',
            question: `Soros bet $10 billion against the British pound, risking his entire fund. How much did he risk as a percentage of his assets?`,
            correctAnswer: [80, 100],
            explanation: `Soros risked everything on one trade - nearly 100% of his fund. This "bet the farm" strategy worked but could have destroyed him. High conviction + massive risk = legendary gains or total ruin.`,
            points: 200,
            bias: 'overconfidence',
            acceptableRange: [50, 100],
            hint: 'He literally bet his entire fund'
          },
          {
            id: 'legend-3',
            title: 'Tiger Woods of Trading',
            description: 'Jesse Livermore made and lost $100 million+ multiple times in the early 1900s.',
            type: 'choice',
            question: `Jesse Livermore, the greatest trader ever, made $100M+ three different times - and lost it all three times. He eventually committed suicide broke. What killed his success?`,
            options: [
              'Bad stock picks',
              'Market crashes he couldn\'t predict',
              'Overconfidence and poor risk management',
              'He wasn\'t actually that good'
            ],
            correctAnswer: [2],
            explanation: `Livermore's genius was reading market psychology, but success bred overconfidence. He'd bet everything on single trades. Talent without discipline = eventual ruin.`,
            points: 200,
            bias: 'overconfidence'
          },
          {
            id: 'legend-4',
            title: 'The Dot-Com Destruction',
            description: 'In 2000, even legendary fund managers lost 50-90% during the dot-com crash.',
            type: 'choice',
            question: `During the dot-com crash, legendary managers like Julian Robertson (Tiger Fund) lost billions and shut down. What destroyed even the pros?`,
            options: [
              'They were bad at picking stocks',
              'They refused to adapt to the "new economy"',
              'Momentum and growth bias in a changing market',
              'They panicked and sold at the bottom'
            ],
            correctAnswer: [2],
            explanation: `Even legends get trapped in biases. Growth managers couldn't adapt when momentum died. Markets change, strategies must evolve or die.`,
            points: 200,
            bias: 'confirmation-bias'
          },
          {
            id: 'legend-5',
            title: 'Long-Term Capital\'s Short Life',
            description: 'LTCM had 2 Nobel Prize winners and "risk-free" models. They lost 90% in 4 months.',
            type: 'choice',
            question: `Long-Term Capital Management had Nobel Prize winners, perfect models, and generated 40% returns. Then they lost 90% in 1998. What happened?`,
            options: [
              'Their models were wrong',
              'Black swan events their models couldn\'t predict',
              'Massive leverage amplified a small mistake',
              'Market manipulation by competitors'
            ],
            correctAnswer: [2],
            explanation: `LTCM used 30:1 leverage. When models failed slightly, leverage turned small losses into fund-killing disasters. Even Nobel Prize winners can blow up with too much leverage.`,
            points: 250,
            bias: 'overconfidence'
          }
        ]
      }
    ]
  },
  {
    id: 'sports-betting-psychology',
    title: "🏈 Sports Betting Psychology",
    description: "Master the mental game where emotion and bias destroy even the sharpest sports minds.",
    badge: 'Sharp Bettor',
    stages: [
      {
        id: 'sports-mind-games',
        title: 'Stage 1: Sports Mind Games',
        description: 'Overcome the psychological traps that turn sports knowledge into gambling losses',
        challenges: [
          {
            id: 'sports-1',
            title: 'The Home Team Bias',
            description: 'You\'re betting on your favorite team in the playoffs.',
            type: 'choice',
            question: `Your team is in the playoffs but favored by only 3 points despite being "obviously better." You know more about this team than any other. Should you bet?`,
            options: [
              'Bet big - you know this team better than Vegas',
              'Avoid betting on your favorite team entirely',
              'Bet the opponent to hedge your emotions',
              'Only bet if the line seems way off'
            ],
            correctAnswer: [1],
            explanation: `Home team bias makes you overconfident and miss red flags. Even experts can't beat the market when emotions cloud judgment. The best bettors avoid their favorite teams.`,
            points: 150,
            bias: 'confirmation-bias'
          },
          {
            id: 'sports-2',
            title: 'The Hot Streak Illusion',
            description: 'A player just scored 30+ points in 5 straight games.',
            type: 'estimate',
            question: `Jayson Tatum scored 30+ in 5 straight games. What's the probability he scores 30+ in game 6?`,
            correctAnswer: [25, 35],
            explanation: `The "hot hand" feels real but often regresses to mean. Tatum's season average matters more than recent streak. Chasing streaks is how bettors go broke.`,
            points: 150,
            bias: 'availability',
            acceptableRange: [15, 50],
            hint: 'What\'s his season average?'
          },
          {
            id: 'sports-3',
            title: 'The Revenge Game Narrative',
            description: 'Tom Brady returns to face his former team after a bitter departure.',
            type: 'choice',
            question: `Brady returns to New England after leaving for Tampa Bay. Sports media hyping "revenge game." He's extra motivated. How should this affect your bet?`,
            options: [
              'Bet Brady - extra motivation means better performance',
              'Fade Brady - too much pressure and emotion',
              'Ignore the narrative - it\'s priced into the line',
              'Bet the under - these games are always sloppy'
            ],
            correctAnswer: [2],
            explanation: `"Revenge games" sound compelling but rarely perform as expected. Vegas knows these narratives and prices them in. Emotional stories lose to statistical analysis.`,
            points: 200,
            bias: 'narrative-fallacy'
          },
          {
            id: 'sports-4',
            title: 'The Chase Strategy',
            description: 'You\'re down $500 after a bad weekend of betting.',
            type: 'choice',
            question: `You lost $500 betting NFL games this weekend. Monday Night Football has a "sure thing" - Mahomes at home vs. a terrible defense. What's your play?`,
            options: [
              'Bet $500 to break even for the week',
              'Bet $1000 to make up for the losses',
              'Skip this game and reset next week',
              'Bet smaller until you rebuild confidence'
            ],
            correctAnswer: [2],
            explanation: `"Chase betting" is how sharp bettors become broke gamblers. No bet is ever a sure thing. Taking time off after losses prevents emotional spiral into bigger losses.`,
            points: 200,
            bias: 'loss-aversion'
          },
          {
            id: 'sports-5',
            title: 'The Public Money Trap',
            description: '85% of public money is on the Warriors in a playoff game.',
            type: 'choice',
            question: `Game 7: Warriors vs. Nuggets. 85% of public bets are on the Warriors. The line hasn't moved much. What does this tell you?`,
            options: [
              'Bet Warriors - the public is right this time',
              'Bet Nuggets - fade the public',
              'Sharp money is offsetting public money',
              'Avoid the game - too unpredictable'
            ],
            correctAnswer: [2],
            explanation: `When heavy public money doesn't move the line, smart money is taking the other side. Sportsbooks need balanced action. Follow the sharp money, not the public.`,
            points: 250,
            bias: 'social-proof'
          },
          {
            id: 'sports-6',
            title: 'The Sunk Cost Cage',
            description: 'You\'ve spent 3 years following a betting system that used to work.',
            type: 'choice',
            question: `You spent 3 years perfecting a betting system that won 58% for 2 years. This year you're down $8,000 and hitting only 45%. The edge seems gone. What do you do?`,
            options: [
              'Keep using it - you\'ve invested too much time to quit',
              'Abandon the system - the edge is gone',
              'Bet smaller while you figure out what changed',
              'Study why it stopped working before deciding'
            ],
            correctAnswer: [1, 3],
            explanation: `This is "The Sunk Cost Cage" - continuing a failing system because of past investment. Markets evolve, edges disappear. Your 3 years of work is a sunk cost. Adapt or die.`,
            points: 250,
            bias: 'sunk-cost-fallacy'
          }
        ]
      }
    ]
  },
  {
    id: 'crypto-casino',
    title: "₿ Crypto Casino",
    description: "Navigate the wild west of crypto where fortunes are made and lost in minutes.",
    badge: 'Diamond Hands',
    stages: [
      {
        id: 'crypto-madness',
        title: 'Stage 1: Crypto Madness',
        description: 'Survive the psychological warfare of cryptocurrency markets',
        challenges: [
          {
            id: 'crypto-1',
            title: 'The Altcoin Roulette',
            description: 'A new altcoin is up 2,000% this week with celebrity endorsements.',
            type: 'choice',
            question: `"SafeMoonDoge" is up 2,000% this week. Elon tweeted about it. Influencers calling it "the next Bitcoin." $100 could become $50,000. What do you do?`,
            options: [
              'Throw $1,000 at it - this could be life-changing',
              'Put in $100 you can afford to lose',
              'Research the team and tokenomics first',
              'Avoid - sounds like a pump and dump'
            ],
            correctAnswer: [3],
            explanation: `Most altcoins with "Safe" and "Moon" in the name are pump and dumps. Celebrity endorsements are often paid. When something sounds too good to be true in crypto, it usually is.`,
            points: 150,
            bias: 'fomo'
          },
          {
            id: 'crypto-2',
            title: 'The Diamond Hands Test',
            description: 'Your crypto portfolio is down 70% from all-time highs.',
            type: 'choice',
            question: `Your crypto portfolio went from $50,000 to $15,000. Crypto Twitter keeps saying "diamond hands" and "buy the dip." What's your move?`,
            options: [
              'HODL - diamond hands forever',
              'Buy more - it\'s on sale',
              'Sell some to preserve capital',
              'Depends on your original investment thesis'
            ],
            correctAnswer: [3],
            explanation: `"Diamond hands" culture ignores risk management. If your thesis changed or you're risking money you need, cutting losses makes sense. Slogans don't pay bills.`,
            points: 200,
            bias: 'loss-aversion'
          },
          {
            id: 'crypto-3',
            title: 'The NFT Mania',
            description: 'Everyone in your group chat is flipping NFTs for massive profits.',
            type: 'estimate',
            question: `Your friends are making $10K+ flipping NFTs. BAYC average sale is $100K. What percentage of NFT buyers actually make money long-term?`,
            correctAnswer: [10, 20],
            explanation: `Most NFT buyers lose money. Like any speculative bubble, early adopters win while late buyers hold worthless JPEGs. Only 10-20% make money long-term.`,
            points: 200,
            bias: 'availability',
            acceptableRange: [5, 30],
            hint: 'How many people win in speculative bubbles?'
          },
          {
            id: 'crypto-4',
            title: 'The Leverage Liquidation',
            description: 'You can trade Bitcoin with 100x leverage on this new exchange.',
            type: 'choice',
            question: `A crypto exchange offers 100x leverage on Bitcoin. You could turn $1,000 into $100,000 if Bitcoin moves 1%. What's the catch?`,
            options: [
              'No catch - it\'s free money if you\'re right',
              'You get liquidated if Bitcoin moves 1% against you',
              'The exchange takes huge fees',
              'It\'s illegal in your country'
            ],
            correctAnswer: [1],
            explanation: `100x leverage means a 1% move against you = 100% loss. Crypto is volatile. Most leveraged traders get liquidated within days. High leverage = guaranteed destruction.`,
            points: 200,
            bias: 'overconfidence'
          },
          {
            id: 'crypto-5',
            title: 'The DeFi Yield Farm',
            description: 'A new DeFi protocol offers 500% APY for staking your tokens.',
            type: 'choice',
            question: `"YieldFarmCoin" offers 500% APY for staking. The smart contract is "audited" and the team is "doxxed." TVL is growing fast. What's the red flag?`,
            options: [
              'APY is too high to be sustainable',
              'All new DeFi protocols are scams',
              'Audits guarantee nothing can go wrong',
              'The team being known makes it safe'
            ],
            correctAnswer: [0],
            explanation: `500% APY is unsustainable without massive risk. Most high-yield farms are Ponzi schemes that collapse when new money stops flowing. If it sounds too good to be true...`,
            points: 250,
            bias: 'greed'
          },
          {
            id: 'crypto-6',
            title: 'The Sunk Cost Cage',
            description: 'You\'ve spent 2 years learning about a cryptocurrency that keeps declining.',
            type: 'choice',
            question: `You spent 2 years researching "EthereumKiller" and bought at $100. It's now $15 and the technology isn't working as promised. But you know more about this project than anyone. What do you do?`,
            options: [
              'Hold because you\'ve invested so much time learning about it',
              'Buy more because your research gives you an edge',
              'Sell and cut losses - past time invested doesn\'t matter',
              'Hold until break-even to justify the time spent'
            ],
            correctAnswer: [2],
            explanation: `This is "The Sunk Cost Cage" - continuing something that no longer serves you just because of past investment. Your 2 years of research is a sunk cost. What matters is future potential, not past time invested.`,
            points: 250,
            bias: 'sunk-cost-fallacy'
          }
        ]
      }
    ]
  },
  {
    id: 'market-crash-survival',
    title: "📉 Market Crash Survival",
    description: "Learn to thrive when everyone else is panicking. Crashes create millionaires and destroy fortunes.",
    badge: 'Crisis Alpha',
    stages: [
      {
        id: 'crash-psychology',
        title: 'Stage 1: Crash Psychology',
        description: 'Master the mental game when markets are falling apart',
        challenges: [
          {
            id: 'crash-1',
            title: 'The COVID Crash',
            description: 'March 2020: Markets are down 30% in 3 weeks. Experts predict depression.',
            type: 'choice',
            question: `March 2020: S&P 500 dropped 30% in 3 weeks. News screaming "worst crash since 1929." Your portfolio is destroyed. What do you do?`,
            options: [
              'Sell everything before it gets worse',
              'Buy the dip - stocks are on sale',
              'Wait for the bottom before buying',
              'Dollar cost average during the decline'
            ],
            correctAnswer: [1, 3],
            explanation: `March 2020 was the best buying opportunity in decades. Those who bought the dip or DCA'd during the crash saw massive gains. Selling at the bottom locked in losses.`,
            points: 200,
            bias: 'availability'
          },
          {
            id: 'crash-2',
            title: 'The Media Panic Machine',
            description: 'CNBC shows red screens and "MARKET MELTDOWN" graphics all day.',
            type: 'choice',
            question: `During crashes, financial media shows red screens, scary graphics, and panic interviews. How should this affect your decisions?`,
            options: [
              'Follow the news - they know what\'s happening',
              'Ignore the media completely',
              'Use media panic as a contrarian indicator',
              'Only watch for factual updates'
            ],
            correctAnswer: [2],
            explanation: `Media makes money from fear and clicks, not good investment advice. Peak media panic often coincides with market bottoms. When everyone's terrified, smart money is buying.`,
            points: 150,
            bias: 'availability'
          },
          {
            id: 'crash-3',
            title: 'The Flash Crash Algorithm',
            description: 'The market drops 10% in 30 minutes due to algorithmic trading.',
            type: 'choice',
            question: `The market drops 10% in 30 minutes due to algorithmic selling. Your stop losses trigger automatically. What happened?`,
            options: [
              'Fundamental news caused the crash',
              'Algorithms created a temporary distortion',
              'This is the start of a bear market',
              'Someone knows something you don\'t'
            ],
            correctAnswer: [1],
            explanation: `Flash crashes are often algorithmic glitches, not fundamental changes. Smart traders buy these temporary distortions while others panic. Technology can create opportunities.`,
            points: 200,
            bias: 'availability'
          },
          {
            id: 'crash-4',
            title: 'The Margin Call Massacre',
            description: 'Your broker calls demanding more money or they\'ll liquidate your positions.',
            type: 'choice',
            question: `You're using margin during a crash. Your broker calls: "Add $10,000 or we liquidate your positions at the low." What do you do?`,
            options: [
              'Add more money to save your positions',
              'Let them liquidate - preserve cash',
              'Negotiate more time to raise funds',
              'Panic and sell everything yourself'
            ],
            correctAnswer: [1],
            explanation: `Margin calls during crashes force you to sell at the worst possible time. Better to preserve cash and live to fight another day than chase good money after bad.`,
            points: 200,
            bias: 'loss-aversion'
          },
          {
            id: 'crash-5',
            title: 'The Recovery Rally',
            description: 'After months of decline, the market suddenly rallies 20% in two weeks.',
            type: 'choice',
            question: `After a brutal bear market, stocks suddenly rally 20% in two weeks. Media calls it a "recovery." Is this the bottom?`,
            options: [
              'Yes - the worst is over',
              'No - this is a dead cat bounce',
              'Maybe - need more data to confirm',
              'Doesn\'t matter - stick to your plan'
            ],
            correctAnswer: [2, 3],
            explanation: `Bear market rallies are common and brutal for shorts. Could be a bottom or dead cat bounce. Rather than predict, stick to your systematic approach and let time reveal the truth.`,
            points: 250,
            bias: 'confirmation-bias'
          }
        ]
      }
    ]
  },
  {
    id: 'options-assassin',
    title: "🎲 Options Assassin",
    description: "Master the most dangerous weapons in finance. Options can create or destroy wealth instantly.",
    badge: 'Volatility Hunter',
    stages: [
      {
        id: 'options-warfare',
        title: 'Stage 1: Options Warfare',
        description: 'Learn to wield options without blowing up your account',
        challenges: [
          {
            id: 'options-1',
            title: 'The Weekly Lottery Ticket',
            description: 'Tesla calls expiring Friday could 10x if the stock moves 5%.',
            type: 'choice',
            question: `Tesla weekly calls cost $100 each. If Tesla moves 5% this week, they could be worth $1,000. Tesla moves 5%+ about 30% of weeks. Good bet?`,
            options: [
              'Yes - 30% chance to make 10x is great odds',
              'No - you lose 70% of the time',
              'Maybe - depends on your risk tolerance',
              'This is gambling, not investing'
            ],
            correctAnswer: [1, 3],
            explanation: `Weekly options are lottery tickets. Even with 30% win rate, time decay and volatility crush destroy most options buyers. Most profitable options traders are sellers, not buyers.`,
            points: 150,
            bias: 'overconfidence'
          },
          {
            id: 'options-2',
            title: 'The Gamma Squeeze',
            description: 'AMC call options are causing a "gamma squeeze" driving the stock higher.',
            type: 'choice',
            question: `AMC is squeezing higher due to massive call buying forcing market makers to buy shares. You can join the squeeze or sell puts. What's the risk?`,
            options: [
              'No risk - the squeeze will continue',
              'Gamma squeezes can reverse instantly',
              'Market makers always win',
              'Options volume doesn\'t affect stock prices'
            ],
            correctAnswer: [1],
            explanation: `Gamma squeezes are violent but temporary. When call buying stops, market makers sell their hedges and the stock crashes. Most retail traders buy at the top of squeezes.`,
            points: 200,
            bias: 'fomo'
          },
          {
            id: 'options-3',
            title: 'The Covered Call Trap',
            description: 'You own 100 shares of Apple and want to "generate income" selling calls.',
            type: 'choice',
            question: `You own 100 AAPL shares at $150. You can sell $160 calls for $500 premium. If AAPL hits $160, you keep the premium but lose upside. Smart strategy?`,
            options: [
              'Yes - free money if AAPL stays below $160',
              'No - you cap your upside for small premium',
              'Depends on your outlook for AAPL',
              'Only if you want to sell the shares anyway'
            ],
            correctAnswer: [2, 3],
            explanation: `Covered calls work in sideways markets but cap upside in bull markets. You get paid to potentially sell your shares. Only use if you're neutral/slightly bearish.`,
            points: 200,
            bias: 'income-bias'
          },
          {
            id: 'options-4',
            title: 'The Iron Condor Nightmare',
            description: 'You sold an iron condor expecting low volatility, but earnings caused a big move.',
            type: 'choice',
            question: `You sold an iron condor on NVDA expecting it to stay between $200-220. Earnings caused a move to $240. Your max loss is $1,000. What now?`,
            options: [
              'Hold and hope it comes back',
              'Close the trade and take the loss',
              'Roll the trade to next month',
              'Double down and sell more condors'
            ],
            correctAnswer: [1],
            explanation: `When your thesis is wrong, take the loss quickly. Iron condors profit from low volatility - when volatility explodes, cut losses fast rather than hope for mean reversion.`,
            points: 200,
            bias: 'loss-aversion'
          },
          {
            id: 'options-5',
            title: 'The Volatility Crush',
            description: 'You bought calls before earnings. The stock went up but your calls lost money.',
            type: 'choice',
            question: `You bought calls before AMZN earnings. AMZN beat and went up 3%, but your calls lost 20%. What happened?`,
            options: [
              'The options were overpriced',
              'Implied volatility dropped after earnings',
              'Time decay ate the premium',
              'Market makers manipulated the price'
            ],
            correctAnswer: [1],
            explanation: `Volatility crush: Options are expensive before earnings due to uncertainty. After earnings, uncertainty disappears and option prices collapse even if the stock moves favorably.`,
            points: 250,
            bias: 'overconfidence'
          }
        ]
      }
    ]
  },
  {
    id: 'everyday-decision-lab',
    title: "\uD83E\uDDE0 Everyday Decision Lab",
    description: "Discover the hidden biases sabotaging your daily choices — from what you eat to where you work.",
    badge: 'Life Optimizer',
    stages: [
      {
        id: 'daily-bias-bootcamp',
        title: 'Stage 1: Daily Bias Bootcamp',
        description: 'Uncover the cognitive traps hiding in your everyday decisions',
        challenges: [
          {
            id: 'everyday-1',
            title: 'The Salary Anchor',
            description: 'You are negotiating salary for a new job and the recruiter throws out a number first.',
            type: 'choice',
            question: `You're interviewing for a senior role. The recruiter asks your salary expectations and immediately adds: "The budget for this role is around $95,000." You were going to ask for $120,000 based on market research. What should you do?`,
            options: [
              'Accept $95,000 — the company clearly can\'t pay more',
              'Counter at $100,000 — meet them close to their number',
              'State your researched number of $120,000 and justify it with data',
              'Ask them to remove the anchor and discuss total compensation'
            ],
            correctAnswer: [2, 3],
            explanation: `This is textbook anchoring bias. The recruiter's $95K number "anchors" your brain downward, making $100K feel like a win when you're actually leaving $20K on the table. Studies show the first number in a negotiation disproportionately influences the final outcome. Always anchor high with data, or explicitly reject the anchor.`,
            points: 100,
            bias: 'anchoring',
            hint: 'Who benefits when the other side sets the first number?'
          },
          {
            id: 'everyday-2',
            title: 'The Streaming Paralysis',
            description: 'You have 4 streaming services and 500+ shows in your watchlist but can\'t pick one.',
            type: 'estimate',
            question: `Research shows that when people are given more options, satisfaction with their final choice actually decreases. In a famous jam study, a store displayed either 6 or 24 jam varieties. What percentage of people bought jam when shown 24 options vs. 6 options?`,
            correctAnswer: [3, 5],
            explanation: `Only about 3% of people bought when shown 24 jams, versus 30% when shown 6. This is the Paradox of Choice (also called choice overload). More options create decision fatigue, fear of choosing wrong, and ultimately paralysis. This is why Netflix auto-plays recommendations and restaurants with smaller menus have happier customers.`,
            points: 125,
            bias: 'choice-overload',
            acceptableRange: [1, 15],
            hint: 'Think about how overwhelmed you feel scrolling through Netflix'
          },
          {
            id: 'everyday-3',
            title: 'The Relationship That Won\'t End',
            description: 'You\'ve been in a relationship for 4 years. It\'s not working, but you\'ve invested so much.',
            type: 'choice',
            question: `You've been dating someone for 4 years. You've met each other's families, moved in together, adopted a dog. But you're unhappy and have been for a year. Your friend says "you've invested too much to leave." What's the rational move?`,
            options: [
              'Stay — you\'ve invested 4 years and can\'t get that time back',
              'Leave — past time invested shouldn\'t determine future happiness',
              'Give it 6 more months since you\'ve already given it 4 years',
              'Stay because starting over would "waste" those 4 years'
            ],
            correctAnswer: [1],
            explanation: `This is the sunk cost fallacy applied to relationships — one of its most painful forms. Those 4 years are gone whether you stay or leave. The only rational question is: "Will the NEXT year be better if I stay or go?" People stay in bad jobs, relationships, and cities for years because leaving feels like "wasting" the time already spent. But staying wastes future time too.`,
            points: 150,
            bias: 'sunk-cost-fallacy',
            hint: 'Can you get those 4 years back either way?'
          },
          {
            id: 'everyday-4',
            title: 'The Headline Trap',
            description: 'News headlines are shaping how you see the world — and you don\'t even notice.',
            type: 'choice',
            question: `You read two headlines about the same medical study. Headline A: "New treatment FAILS to help 30% of patients." Headline B: "New treatment helps 70% of patients." Which framing makes people more likely to support the treatment?`,
            options: [
              'Headline A — people focus more on failures',
              'Headline B — positive framing increases support by up to 20%',
              'Neither — people see through framing to the actual data',
              'Both equally — 70% and 30% failure are the same thing'
            ],
            correctAnswer: [1],
            explanation: `This is the framing effect, discovered by Kahneman and Tversky. Identical information presented as a gain ("saves 70%") vs. a loss ("fails 30%") dramatically shifts decisions. In studies, positive framing increased treatment support by 15-20%. Media outlets exploit this constantly. "Unemployment falls to 4%" vs. "1 in 25 Americans can't find work" — same data, opposite emotional reactions.`,
            points: 175,
            bias: 'framing-effect',
            hint: 'Both headlines describe the exact same study result'
          },
          {
            id: 'everyday-5',
            title: 'The Career Comfort Zone',
            description: 'You have a stable but unfulfilling job. A risky but exciting opportunity appears.',
            type: 'choice',
            question: `You've been at your comfortable corporate job for 7 years. Pay is decent, work is boring. A startup offers you a role: 20% pay cut, equity that could be worth $0 or $500K, and work you're passionate about. 60% of your friends say "don't risk it." What's really driving the decision to stay?`,
            options: [
              'Rational risk assessment — the pay cut is too dangerous',
              'Status quo bias — the comfort of the known feels safer than it is',
              'Good judgment — stability is always the right choice',
              'Social pressure — most people advise against change'
            ],
            correctAnswer: [1],
            explanation: `Status quo bias makes us overvalue our current situation simply because it's familiar. Studies show people require 2-3x the potential gain to accept a change from their current state. Psychologist Daniel Kahneman found that losses from change "feel" twice as painful as equivalent gains feel good. This keeps millions of people in jobs, cities, and situations that no longer serve them — not because they've analyzed the risk, but because "staying" feels like the safe default.`,
            points: 200,
            bias: 'status-quo-bias',
            hint: 'Is "doing nothing" really the safe choice, or does it just feel that way?'
          },
          {
            id: 'everyday-6',
            title: 'The DIY Delusion',
            description: 'You spent 12 hours building a bookshelf. It\'s crooked but you think it\'s beautiful.',
            type: 'estimate',
            question: `In a Harvard study, participants built IKEA furniture themselves. When asked to price their creations vs. identical pre-built furniture, how much MORE did builders value their own wobbly creations compared to the professionally assembled version (as a percentage)?`,
            correctAnswer: [50, 70],
            explanation: `Builders valued their own creations about 63% more than identical expert-built versions. This is the IKEA Effect — we massively overvalue things we helped create, regardless of quality. This bias affects everything: code you wrote (must be better than the library), meals you cooked (better than the restaurant), business ideas you conceived (must be brilliant). It's why founders hold failing startups too long and why "not invented here" syndrome kills good teams.`,
            points: 250,
            bias: 'ikea-effect',
            acceptableRange: [20, 100],
            hint: 'People really love their own wobbly creations'
          }
        ]
      }
    ]
  },
  {
    id: 'casino-psychology',
    title: "\uD83C\uDFB0 Casino Psychology",
    description: "Peek behind the curtain of a billion-dollar industry designed to exploit your brain's blind spots.",
    badge: 'House Edge Master',
    stages: [
      {
        id: 'house-always-wins',
        title: 'Stage 1: The House Always Wins',
        description: 'Learn exactly how casinos exploit cognitive biases to separate you from your money',
        challenges: [
          {
            id: 'casino-1',
            title: 'The Almost-Winner',
            description: 'The slot machine shows two cherries and a near-miss on the third reel.',
            type: 'choice',
            question: `You're playing slots. The reels show Cherry-Cherry-[almost Cherry]. You "just barely" missed the jackpot. This makes you want to keep playing. But here's the thing: modern slot machines are programmed to show near-misses more often than random chance would produce. Why do casinos do this?`,
            options: [
              'It\'s a glitch in the random number generator',
              'Near-misses trigger the same brain reward circuits as actual wins',
              'It\'s illegal — casinos can\'t program outcomes',
              'Near-misses are just random and don\'t affect behavior'
            ],
            correctAnswer: [1],
            explanation: `The near-miss effect is one of gambling's most powerful psychological weapons. Brain imaging studies show that near-misses activate the ventral striatum (reward center) almost identically to actual wins. Your brain interprets "almost winning" as evidence you're getting closer, even though each spin is completely independent. Slot machines are legally designed to show near-misses up to 30% of the time — far more than true randomness would produce.`,
            points: 100,
            bias: 'near-miss-effect',
            hint: 'What does your brain feel when you "almost" win?'
          },
          {
            id: 'casino-2',
            title: 'The Red Roulette Run',
            description: 'The roulette ball has landed on black 8 times in a row.',
            type: 'estimate',
            question: `A roulette wheel has landed on BLACK eight times in a row. The crowd is buzzing — "Red is DUE!" What is the actual probability (as a percentage) that the next spin lands on red? (Assume a standard American roulette wheel with 18 red, 18 black, 2 green.)`,
            correctAnswer: [47, 48],
            explanation: `The probability is exactly 47.4% (18/38) — the same as it always is, regardless of previous spins. The wheel has no memory. This is the Gambler's Fallacy: the belief that past random events influence future ones. In 1913 at Monte Carlo, black hit 26 times in a row. Gamblers lost millions betting on red, convinced it was "due." Each spin is independent. The universe doesn't keep score.`,
            points: 150,
            bias: 'gamblers-fallacy',
            acceptableRange: [40, 55],
            hint: 'Does the roulette wheel remember what happened last spin?'
          },
          {
            id: 'casino-3',
            title: 'The Skilled Gambler Illusion',
            description: 'A blackjack player claims they can "beat the house" with their system.',
            type: 'choice',
            question: `Your friend claims he's a "winning" blackjack player who beats the house. He plays 3 nights a week and keeps detailed records showing he's up $5,000 this year. He plays basic strategy and bets bigger when he "feels" a hot streak coming. What's most likely true?`,
            options: [
              'He\'s genuinely skilled — basic strategy can beat the house',
              'He\'s on a lucky streak that will eventually regress to a net loss',
              'Card counting is the only way to beat blackjack, and he\'s not doing it',
              'His record-keeping is probably biased toward remembering wins'
            ],
            correctAnswer: [1, 3],
            explanation: `The house edge in blackjack with perfect basic strategy is still 0.5-1%. Without card counting, you CANNOT beat the house long-term — the math is unbeatable. Your friend is likely experiencing a positive variance streak AND selectively remembering wins (confirmation bias in record-keeping). Studies show recreational gamblers overestimate their winnings by 30-50%. The illusion of skill keeps people playing games of chance.`,
            points: 175,
            bias: 'illusion-of-control',
            hint: 'Can perfect strategy overcome a built-in mathematical edge?'
          },
          {
            id: 'casino-4',
            title: 'The Chasing Spiral',
            description: 'You\'re down $300 at the poker table. You can feel the urge to win it back.',
            type: 'choice',
            question: `You're at a casino poker table, down $300 for the night. You told yourself you'd leave at -$200 but kept playing. Now you're thinking: "I just need one good hand to get back to even." The logical part of your brain is screaming to leave. What psychological trap is operating?`,
            options: [
              'Loss aversion — losses hurt 2x more than gains feel good',
              'The break-even effect — our brains desperately want to avoid "closing" a loss',
              'Escalation of commitment — each dollar lost makes quitting feel worse',
              'All of the above, working together to keep you in your seat'
            ],
            correctAnswer: [3],
            explanation: `Loss chasing is a devastating cocktail of multiple biases: Loss aversion makes the $300 loss feel like $600 of pain. The break-even effect creates an intense desire to get back to zero before stopping. Escalation of commitment means each additional dollar lost makes it psychologically harder to walk away. Casinos know this — that's why there are no clocks, no windows, and free drinks. They're not being generous; they're removing every possible cue that might remind you to stop.`,
            points: 200,
            bias: 'loss-chasing',
            hint: 'Is there just ONE bias at work here, or several?'
          },
          {
            id: 'casino-5',
            title: 'The Hot Dice Myth',
            description: 'A craps shooter has rolled winners 12 times straight. The table is electric.',
            type: 'choice',
            question: `At the craps table, a shooter has hit their point 12 times in a row. The crowd is going wild. People are pressing their bets. A stranger tells you "this guy's got the hot hand — ride it!" In poker, skilled players do have hot streaks. Does the same apply to craps dice?`,
            options: [
              'Yes — some people develop a "rhythm" throwing dice',
              'No — dice outcomes are purely random regardless of the thrower',
              'Partly — confidence affects physical consistency of the throw',
              'Yes — the hot hand is real in all games of chance'
            ],
            correctAnswer: [1],
            explanation: `The hot hand fallacy is the belief that random success breeds more success. While the hot hand has some validity in skilled activities (basketball shooting, poker decisions), dice rolls are pure physics and randomness. No amount of "rhythm" or confidence changes the mathematical probability of each throw. Casinos love the hot hand illusion at craps — it keeps entire tables betting bigger during streaks that are purely statistical noise. A die doesn't know or care about its last 12 rolls.`,
            points: 225,
            bias: 'hot-hand-fallacy',
            hint: 'Does a pair of dice know what they rolled last time?'
          },
          {
            id: 'casino-6',
            title: 'The Engineered Addict',
            description: 'Everything about a casino is designed to keep you playing. Everything.',
            type: 'choice',
            question: `Casinos have no clocks, no windows, free drinks, oxygen pumped in, labyrinth layouts, and near-miss slot programming. But the MOST powerful psychological trick is something simpler. A player who wins $100, then loses $150 (net -$50) will feel very different from one who loses $150 then wins $100 (also net -$50). Which player is more likely to keep gambling?`,
            options: [
              'Player A (won first, then lost) — they want to "get back" to winning',
              'Player B (lost first, then won) — the recent win creates momentum',
              'Both equally — net outcome is the same',
              'Neither — rational people would both stop at -$50'
            ],
            correctAnswer: [1],
            explanation: `Player A is far more likely to keep gambling. This is the "endowment effect" meets loss aversion: Player A mentally "owned" that $100 profit, then "lost" $150 — their brain codes this as a $150 loss from a peak. Player B's brain codes the journey as "recovering from a loss" — the recent win provides a psychological exit point. Casinos engineer early small wins (loose slots near entrances, beginner's luck at tables) specifically to create this ownership feeling, making subsequent losses feel like something being "taken from you" rather than normal gambling variance.`,
            points: 250,
            bias: 'endowment-effect',
            hint: 'Think about which player feels like something was "taken" from them'
          }
        ]
      }
    ]
  }
];